/**
 * The per-user settings information architecture — the same registry shape as
 * `adminSections.tsx`, for the `/settings` surface.
 *
 * Issue #91, epic #90. `/settings` is today one page stacking three cards
 * (Theme, Profile, Personal Access Tokens). Epic #90 splits it into routed
 * destinations behind the same searchable hub the admin console gets (#96), so
 * it needs the same thing the console needs: ONE declaration read by the hub,
 * the AppBar's title resolver, and anything else that later wants to draw the
 * surface.
 *
 * This file deliberately declares only DATA. The `SettingsCardDef` /
 * `SettingsSectionDef` types and both helpers
 * (`visibleSettingsSections`, `settingsPageTitle`) are the package's
 * (`@marinoscar/platform-web/settings/ui`, #466; `adminSections.tsx` re-exports
 * them for the hub, rail and AppBar) and are re-used verbatim — which is precisely why those
 * helpers take `sections`, `hubPath` and `hubTitle` as parameters instead of
 * closing over the admin constants. Two copies of the permission gate is the
 * drift the registry exists to prevent, and copying it here to serve a second
 * surface would reintroduce it on day one.
 */

import PersonIcon from '@mui/icons-material/Person';
import PaletteIcon from '@mui/icons-material/Palette';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { identityUserSettingsSections } from '@marinoscar/platform-web/identity/ui';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { kvoxGraphUserSettingsSections } from '@marinoscar/kvox-web/graph/ui';
import { kvoxNotesUserSettingsSections } from '@marinoscar/kvox-web/notes/ui';
import { kvoxSearchUserSettingsSections } from '@marinoscar/kvox-web/search/ui';
import { dangerZoneSettingsPage } from '@marinoscar/platform-web/user-data/ui';
import { dataExportSettingsPage } from '@marinoscar/platform-web/exports/ui';
// Webhooks (#795): the per-user card, from the webhooks slice as data.
import { webhooksUserSettingsSections } from '@marinoscar/platform-web/webhooks/ui';
// Getting Started (#279, epic #271) — the same icon the admin `Setup` card
// carries, on purpose: the two are the same idea on two axes (this deployment
// vs. this account), and a user who has seen one recognises the other.
import type { SettingsSectionDef } from '@marinoscar/platform-web/settings/ui';
import { gettingStartedSettingsPage } from '@marinoscar/platform-web/onboarding/ui';

/**
 * The user settings sections, in hub order.
 *
 * NO CARD DECLARES A `permission` — WITH ONE DELIBERATE EXCEPTION, the
 * `Knowledge graph` card (#369), gated on `graph:write` because that is the
 * exact string its attribute-definition write routes enforce (Settings UI rule
 * 3, docs/specs/ontology.md §13). For every other card this is the correct
 * model rather than an omission: every authenticated user owns their own settings, and the API
 * grants `user_settings:read` / `user_settings:write` to all three roles
 * (Admin, Contributor, Viewer). Adding a gate here would be inventing an
 * authorization rule the API does not enforce — the opposite of what this
 * registry is for. `visibleSettingsSections` is still the function the hub
 * calls, so search filtering and empty-section collapsing behave identically
 * to the admin surface; the permission half of the gate simply passes
 * everything through.
 *
 * Access Tokens sits under its own `Security` group rather than under
 * `Account` because a PAT is a long-lived credential: grouping it with display
 * name and theme would put "create a bearer token that outlives your session"
 * one row below "pick a colour scheme".
 */
export const USER_SETTINGS_SECTIONS: SettingsSectionDef[] = [
  {
    label: 'Account',
    cards: [
      {
        // Issue #279, epic #271; the platform's Getting started page since #481. FIRST IN
        // `Account`, AND THEREFORE FIRST IN THE HUB, which is the point of it: this is where the
        // shell banner (#277) leads, and where the checklist REMAINS findable after that banner
        // has been dismissed. A dismissal is permanent by design (`onboarding.checklistDismissedAt`),
        // so the entry point it hides has to survive somewhere a user would think to look, and the
        // top of their own settings is that place.
        //
        // The card is the package's (`gettingStartedSettingsPage.card`) and carries NO
        // `permission`, like every other card in this registry: the platform's
        // `GET /api/onboarding` needs only `user_settings:read`, which every role holds, because the
        // resource is the caller's own activation state. A gate here would invent an authorization
        // rule the API does not enforce, and it would fail in the worst direction: a Viewer, this
        // application's DEFAULT role, shut out of the one page explaining why AI features want a
        // key from them.
        //
        // Under `Account` and not a group of its own: it is the same kind of fact as the cards
        // below it (who this account is, how it is set up), and a group for one card would put a
        // heading above a single row at the top of the hub.
        ...gettingStartedSettingsPage.card,
        Icon: gettingStartedSettingsPage.Icon,
      },
      {
        title: 'Profile',
        description: 'Your display name, profile image and time zone, and the email you signed in with.',
        Icon: PersonIcon,
        path: '/settings/profile',
      },
      {
        title: 'Appearance',
        description: 'Choose a light, dark, or system-matched theme for this account.',
        Icon: PaletteIcon,
        path: '/settings/appearance',
      },
      {
        // Issue #126, epic #109. NO `permission`, like every card here: the
        // page edits the caller's OWN preferences through
        // `PATCH /api/user-settings`, which the API grants to all three roles,
        // and the registry it renders (`GET /api/notifications/events`) is
        // `@Auth()` with no permissions for exactly that reason — gating this
        // card would leave a Viewer unable to say how they are contacted.
        //
        // Under `Account` rather than `Security`, even though one of the events
        // it lists is a security alert: the card is about how this account is
        // contacted, not about credentials. `Security` holds long-lived
        // credentials (see the group's own note below).
        title: 'Notifications',
        description:
          'Choose which events notify you, and whether they arrive by email or in your browser.',
        Icon: NotificationsIcon,
        path: '/settings/notifications',
      },
      {
        // Issue #55, epic #45; the platform's since #477 (epic #472). The page is
        // the platform AI slice's `UserAiKeysPage`
        // (`@marinoscar/platform-web/ai/ui`): the caller's own provider keys
        // (`/api/ai/keys`, which returns a hint, never the key), the models they
        // can use, their default model and their usage.
        //
        // `ai:use` is the exact string the platform's per-user AI routes enforce
        // (seeded to Admin, Contributor and Viewer by #473), and `feature: 'ai'`
        // hides the card while AI is switched off deployment-wide: with AI off
        // there is nothing a key could be used for. Unlike every other card in
        // this file it therefore HAS a gate, because the API has one (before
        // #477 the old `/api/ai-credentials` routes enforced none). The key
        // itself is still the caller's own and billed to their own provider
        // account: no deployment-wide fallback key exists.
        //
        // Under `Account` rather than `Security`, even though it holds a
        // credential. `Security` is for credentials THIS application issues
        // and can revoke (a personal access token). This is a third party's
        // credential, billed to the user's own provider account — it belongs
        // with the other facts about who this account is and how it is set up.
        title: 'AI Provider',
        description:
          'Connect your own AI provider key. AI features run on your account, and the usage is billed to you.',
        Icon: AutoAwesomeIcon,
        path: '/settings/ai',
        permission: 'ai:use',
        feature: 'ai',
      },
      // Search Indexing (#191, epic #165; the slice's since #501, epic #498).
      // The card is DATA exported by `@marinoscar/kvox-web/search/ui`, with no
      // `permission` (the API gates the routes on `@Auth()` alone) and the
      // comment explaining why; it keeps its place directly after `AI Provider`.
      ...kvoxSearchUserSettingsSections.account,
      // Note Templates (#56, epic #45; the slice's since #502, epic #498). The
      // card is DATA exported by `@marinoscar/kvox-web/notes/ui`, with no
      // `permission` (`note_templates:*` is seeded to ALL THREE roles, so a gate
      // would hide the card from nobody while inventing a rule the API does not
      // enforce) and the comment explaining why; it keeps its place directly
      // after Search Indexing, under `Account` alongside `AI Provider`.
      ...kvoxNotesUserSettingsSections.account,
    ],
  },
  {
    // Issue #369, epic #346 (docs/specs/ontology.md §13 — "the only registry
    // entry this feature adds"). A GROUP OF ITS OWN, after `Account`: the
    // connected-knowledge graph is the user's curated knowledge, not a fact
    // about the account, and later knowledge surfaces (imports) will join it
    // here. (#383's personal domain is a switch on this same page, not a card.) One card under its own heading reads as a
    // section rather than as orphaned — the same shape `Security` and
    // `Danger Zone` already have with one card each.
    //
    // THE ONE CARD IN THIS REGISTRY WITH A `permission`, and it is the exact
    // string the API enforces rather than an invented gate (Settings UI rule
    // 3): `graph-attribute-defs.controller.ts` gates its writes on
    // `graph:write`, and the page's attribute browser is its core. The
    // preferences on the same page write through `/api/user-settings`
    // (`user_settings:write`, all roles) — but a user without `graph:write`
    // has no graph to have preferences about. `graph:write` is seeded to all
    // three roles, so in a stock deployment this hides the card from nobody.
    label: 'Knowledge',
    // The card is the graph slice's since #503 (epic #498), spread where it was
    // written out: the `permission` is the exact string the API enforces, see the
    // note above and `kvoxGraphUserSettingsSections`.
    cards: [...kvoxGraphUserSettingsSections.knowledge],
  },
  {
    label: 'Security',
    // Issue #465, epic #462. `Access Tokens` is the identity slice's own
    // registry entry (`/settings/tokens`, no permission), spread where the
    // literal card was.
    cards: [...identityUserSettingsSections.security],
  },
  {
    // Issue #532, Part B of #479: "Download your data", the platform's exports slice
    // (`dataExportSettingsPage` of `@marinoscar/platform-web/exports/ui`; the route is `card.path`,
    // `/settings/data-export`, the page the `export.ready` notification links to). A GROUP OF ITS
    // OWN, directly above `Danger Zone`: the two are the pair of things a user can do with the data
    // held about them (take a copy, delete it), but the `Danger Zone` group stays alone and last
    // (`dangerZoneLastViolations`), so the export cannot join it, and it is a copy, not a
    // credential or an account fact, so it joins neither `Security` nor `Account`.
    //
    // NO `permission`, like every card in this registry: the platform's `/api/exports` routes are
    // `@Auth()`, the permission is the SOURCE's, and both sources the page offers every role
    // (`user-data` and this app's `kvox-content`) require `user_settings:read`, held by Admin,
    // Contributor and Viewer. A source an administrator may additionally use (`org-data`) appears
    // on the same page only for the holder of its own permission, decided by the API.
    label: 'Your data',
    cards: [{ ...dataExportSettingsPage.card, Icon: dataExportSettingsPage.Icon }],
  },
  // #795: `Integrations`, APPENDED before the pinned Danger Zone. The caller's own
  // outbound webhook endpoints. `webhooks:read` is the exact string `/api/webhooks` enforces: an
  // ORG permission every org role holds by default, but a grant a deployment can withhold, so the
  // card mirrors it. Writes (`webhooks:write`) are gated inside the page.
  {
    label: 'Integrations',
    cards: [...webhooksUserSettingsSections.integrations],
  },
  {
    // Issue #80. A THIRD GROUP, LAST, AND ON ITS OWN — not a fourth card under
    // `Account` and not a second one under `Security`. The hub renders groups
    // as visually separated blocks with their own heading, and that separation
    // is the only thing this registry can contribute to safety: a destination
    // whose every action is permanent and unrecoverable must not sit one row
    // under "pick a colour scheme", which is precisely the adjacency the
    // `Security` group's own note above already objected to for a far milder
    // reason (a revocable token this application issued). Last in the array so
    // it is last on the hub and last in the Console rail, in both cases below
    // everything a user has an ordinary reason to open.
    //
    // `Security` would have been the near miss. That group is about credentials
    // — things that can be issued again after being destroyed. Nothing reached
    // from here can be: there is no undo, and no restore a user can trigger.
    //
    // The card, its icon and its page are the PLATFORM's since #480
    // (`dangerZoneSettingsPage` of `@marinoscar/platform-web/user-data/ui`; the
    // route is `card.path`, `/settings/danger-zone`). Which scopes it offers, and
    // what each deletes, come from the API's user-data registries
    // (`apps/api/src/platform/user-data/user-data.manifest.ts`), not from this file.
    //
    // NO `permission`, like every card in this registry, and here the API's own
    // shape is the argument rather than a convention this file follows: the
    // platform user-data controller gates `GET /api/user-data/summary` and
    // `POST /api/user-data/deletions` on `@Auth()` with NO permission string,
    // because the resource is the caller's OWN data, scoped by `userId` in the
    // query itself. Declaring a gate here would invent an authorization rule the
    // API does not enforce, and it would fail in the worst available direction: a
    // user unable to delete data this deployment is holding about them, because
    // of a role somebody else assigned. Kept LAST (`dangerZoneLastViolations`).
    label: 'Danger Zone',
    cards: [{ ...dangerZoneSettingsPage.card, Icon: dangerZoneSettingsPage.Icon }],
  },
];

/**
 * The user settings hub — the one `/settings` route that owns no card.
 *
 * `USER_HUB_TITLE` is intentionally the same string as `ADMIN_HUB_TITLE`
 * ('Settings'): the two surfaces are never on screen at once, the path
 * disambiguates them for the title resolver, and calling this one "My
 * Settings" in the AppBar would be the only place in the app that names it
 * that way.
 */
export const USER_HUB_PATH = '/settings';
export const USER_HUB_TITLE = 'Settings';
